import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useUserPlan } from '@/contexts/UserPlanContext'
import { useDisplayName } from '@/hooks/useDisplayName'
import { toast } from 'sonner'
import {
  LogOut,
  AlertCircle,
  ChevronDown,
  CreditCard,
  Loader2,
} from 'lucide-react'
import { getStripeCustomerPortalUrl } from '@/api/avpulse-api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from './button'

export const UserDropdown = () => {
  const { user, signOut } = useAuth()
  const { isFreePlan } = useUserPlan()
  const { displayName } = useDisplayName()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)

  // Get initials from display name
  const getInitials = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return '?'

    const parts = trimmed.split(' ').filter(part => part.length > 0)
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return trimmed.slice(0, 2).toUpperCase()
  }

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true)
    try {
      const { portal_session } = await getStripeCustomerPortalUrl()
      if (portal_session?.url) {
        window.location.href = portal_session.url
      }
    } catch (error) {
      toast.error('Failed to open subscription portal')
      console.error('Failed to fetch stripe portal url:', error)
    } finally {
      setIsLoadingPortal(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      toast.error('Failed to sign out')
    }
  }

  if (!user) {
    return null
  }

  return (
    <DropdownMenu
      open={isLoadingPortal ? true : undefined}
      onOpenChange={open => {
        if (!isLoadingPortal) {
          setIsOpen(open)
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative flex items-center cursor-pointer"
        >
          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground font-medium text-xs">
            {getInitials(displayName || '')}
          </div>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
          {user.email && !user.emailVerified && (
            <AlertCircle className="absolute -top-1 -right-1 h-3 w-3 text-yellow-600 bg-background rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {user.email && !user.emailVerified && (
          <DropdownMenuItem
            onClick={() => navigate('/auth/verify-email')}
            className="text-yellow-600"
          >
            <AlertCircle className="h-4 w-4" />
            Verify Email
          </DropdownMenuItem>
        )}
        {!isFreePlan && (
          <>
            <DropdownMenuItem
              onClick={handleManageSubscription}
              disabled={isLoadingPortal}
              className="cursor-pointer"
            >
              <CreditCard className="h-4 w-4" />
              Manage Subscription{' '}
              {isLoadingPortal && <Loader2 className="h-4 w-4 animate-spin" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          variant="destructive"
          onClick={handleSignOut}
          className="cursor-pointer"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
